
import { useState, useEffect, useCallback, useRef } from "react"
import { useForm, Controller } from "react-hook-form"
import { ArrowLeft, Save, Car, FileText, Info, Search, AlertCircle, CheckCircle, Image, ChevronsUpDown, Pencil } from "lucide-react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn, formatarMoedaInput, parsearMoeda } from "@/lib/utils"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/contexts/AuthContext"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { VeiculoFormSections } from "@/components/vehicle/VeiculoFormSections"
import { toast } from "sonner"
import { DocumentUpload } from "@/components/DocumentUpload"
import PhotoUploadPrincipal from "@/components/PhotoUploadPrincipal"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { getPublicErrorMessage } from "@/lib/errors"
import { consultarPlacaApiBrasil } from "@/lib/consultaPlacaService"
import { consultarValorFIPE, validarPlaca, type HistoricoFIPE } from "@/lib/fipeService"
import type { TablesInsert } from "@/integrations/supabase/types"
import { vehicleBrands, filterBrands } from "@/lib/brandsData"
import { normalizarCombustivel, normalizarPlaca } from "@/lib/veiculoUtils"
import {
  getFipeLookupUserError,
  getPlateLookupUserError,
  PLACA_INVALIDA_ERROR,
  type VehicleLookupUserError,
} from "@/lib/vehicleLookupMessages"
import {
  PHOTO_BUCKET,
  DOCUMENT_BUCKET,
  getUploadStoragePath,
  uploadFileToBucket,
  type UploadableDocument,
  type UploadablePhoto,
} from "@/lib/vehicleStorage"

interface VehicleFormData {
  placa: string
  marca: string
  modelo: string
  versao?: string
  ano: string
  anoModelo?: string
  cor: string
  combustivel: string
  cambio: string
  quilometragem: string
  motor: string
  potencia?: string
  capacidadeCarga?: string
  passageiros?: string
  tipoVeiculo?: string
  cidade?: string
  estado?: string
  situacaoVeiculo?: string
  portas: string
  observacoes: string
  chassi: string
  renavam: string
  valorFIPE?: string
  foto_principal?: string
}

type ApiVeiculoExtra = {
  chassi?: string
  combustivel?: string
  motor?: string
  potencia?: string | number
  uf_placa?: string
  quantidade_passageiro?: string | number
  capacidade_carga?: string | number
  situacao_veiculo?: string
  situacao_veiculo_descricao?: string
  municipio?: { municipio?: string }
  tipo_veiculo?: { tipo_veiculo?: string }
  cor_veiculo?: { cor?: string }
}

type ApiVeiculo = Record<string, unknown> & {
  placa?: string
  chassi?: string
  marca?: string
  MARCA?: string
  modelo?: string
  MODELO?: string
  versao?: string
  VERSAO?: string
  SUBMODELO?: string
  ano?: string | number
  ano_fabricacao?: string | number
  ano_modelo?: string | number
  anoModelo?: string | number
  cor?: string
  combustivel?: string
  potencia?: string | number
  quantidade_lugares?: string | number
  quantidade_passageiro?: string | number
  quantidade_portas?: string | number
  numero_motor?: string
  motor?: string
  tipo_veiculo?: string | { tipo_veiculo?: string }
  uf_jurisdicao?: string
  uf_placa?: string
  uf?: string
  cidade?: string
  situacao_veiculo?: string
  logo?: string
  extra?: ApiVeiculoExtra
}

const humanizarCombustivel = (valor: string | undefined) => {
  if (!valor) return ''
  const map: Record<string, string> = {
    alcoolgasolina: 'Álcool/Gasolina',
    alcoolegasolina: 'Álcool/Gasolina',
    alcoolgas: 'Álcool/Gasolina',
    alcoogasolina: 'Álcool/Gasolina',
    gasolina: 'Gasolina',
    etanol: 'Etanol',
    alcool: 'Etanol',
    flex: 'Flex',
    diesel: 'Diesel',
    gnv: 'GNV',
    eletrico: 'Elétrico',
    hibrido: 'Híbrido',
  }
  const key = valor.toLowerCase().replace(/[\s\-/\\]+/g, '').replace(/_/g, '')
  return map[key] ?? (valor.charAt(0).toUpperCase() + valor.slice(1).toLowerCase())
}

const humanizarTextoTecnico = (valor: unknown) => {
  if (valor === null || valor === undefined || valor === '') return ''
  return String(valor)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase())
}

const NovoVeiculo = () => {
  const { register, handleSubmit, setValue, control, formState: { errors } } = useForm<VehicleFormData>({
    defaultValues: {
      marca: "",
      ano: "",
      cor: "",
      cambio: "",
      portas: "4" // Valor padrão para número de portas
    }
  })
  const { loja } = useAuth()
  const vertical = useTipoOperacao()
  const usaFIPE = vertical.documentos.mostrarValoresFinanceiros
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitInFlightRef = useRef(false)
  const [documents, setDocuments] = useState<UploadableDocument[]>([])
  const [fotoPrincipal, setFotoPrincipal] = useState<UploadablePhoto | string | null>(null)
  const [dadosCarregados, setDadosCarregados] = useState(false)
  const [cadastroManual, setCadastroManual] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Estados para consulta de placa
  const [placaConsulta, setPlacaConsulta] = useState("")
  const [loadingConsulta, setLoadingConsulta] = useState(false)
  const [veiculoConsulta, setVeiculoConsulta] = useState<ApiVeiculo | null>(null)
  const [valorFIPE, setValorFIPE] = useState<string | null>(null)
  const [valorFIPENumerico, setValorFIPENumerico] = useState<number | null>(null)
  const [historicoFIPE, setHistoricoFIPE] = useState<HistoricoFIPE[] | null>(null)
  const [mostrarHistorico, setMostrarHistorico] = useState(false)
  const [errorConsulta, setErrorConsulta] = useState<VehicleLookupUserError | null>(null)
  const [errorFIPE, setErrorFIPE] = useState<VehicleLookupUserError | null>(null)
  const [brandOpen, setBrandOpen] = useState(false)
  const [brandSearchValue, setBrandSearchValue] = useState("")
  const [revisaoAberta, setRevisaoAberta] = useState(false)
  const [foiRevisado, setFoiRevisado] = useState(false)

  // Verificar se o veículo já existe
  const verificarVeiculoExistente = useCallback(async (placa: string) => {
    try {
      if (!loja?.id) {
        return false
      }

      const placaNormalizada = normalizarPlaca(placa)
      console.log("[DEBUG] verificarVeiculoExistente placa:", placa, "normalizada:", placaNormalizada)
      const { data, error } = await supabase
        .from('veiculos')
        .select('id')
        .eq('loja_id', loja.id)
        .eq('placa', placaNormalizada)
        .maybeSingle()
      console.log("[DEBUG] verificarVeiculoExistente result:", { data, error })
      return !error && data !== null
    } catch {
      return false
    }
  }, [loja?.id]);

  // Função para consultar placa e FIPE simultaneamente
  const consultarPlaca = async () => {
    if (!placaConsulta.trim()) {
      toast.error("Por favor, informe a placa do veículo")
      return
    }

    const placaNormalizada = normalizarPlaca(placaConsulta)
    if (!validarPlaca(placaNormalizada)) {
      setErrorConsulta(PLACA_INVALIDA_ERROR)
      setErrorFIPE(null)
      setVeiculoConsulta(null)
      setValorFIPE(null)
      setValorFIPENumerico(null)
      toast.error(PLACA_INVALIDA_ERROR.title)
      return
    }

    setLoadingConsulta(true)
    setErrorConsulta(null)
    setErrorFIPE(null)
    setVeiculoConsulta(null)
    setValorFIPE(null)
    setValorFIPENumerico(null)
    setFoiRevisado(false)
    setRevisaoAberta(false)

    try {
      // Consulta de placa (sempre)
      const resultadoPlaca = await consultarPlacaApiBrasil(placaNormalizada)

      // Consulta FIPE apenas para loja (oficina não usa valor de mercado)
      let resultadoFIPE: Awaited<ReturnType<typeof consultarValorFIPE>> | null = null
      if (usaFIPE) {
        await new Promise(resolve => setTimeout(resolve, 500))
        resultadoFIPE = await consultarValorFIPE(placaNormalizada)
      }

      // Processar resultado da consulta de placa
      if (!resultadoPlaca.error && resultadoPlaca.response) {
        setVeiculoConsulta(resultadoPlaca.response)
        setDadosCarregados(true)
        setCadastroManual(false)
      } else {
        setErrorConsulta(getPlateLookupUserError(resultadoPlaca.message))
      }

      // Processar resultado da consulta FIPE (apenas se foi feita)
      if (resultadoFIPE) {
        if (!resultadoFIPE.error && resultadoFIPE.valorFIPE) {
          setValorFIPE(resultadoFIPE.valorFIPE)
          setValorFIPENumerico(resultadoFIPE.valorNumerico ?? null)
          setHistoricoFIPE(resultadoFIPE.historicoFIPE || null)
        } else {
          setErrorFIPE(getFipeLookupUserError(resultadoFIPE.message))
        }
      }

      // Mostrar mensagem de sucesso se a consulta de placa funcionou
      if (!resultadoPlaca.error && resultadoPlaca.response) {
        toast.success("Consulta realizada com sucesso!")
      } else if (resultadoFIPE && !resultadoFIPE.error && resultadoFIPE.valorFIPE) {
        toast.success("Consulta realizada com sucesso!")
      } else {
        toast.error("Não foi possível preencher os dados automaticamente.")
      }
    } catch (err) {
      const userError = getPlateLookupUserError()
      setErrorConsulta(userError)
      toast.error(userError.title)
    } finally {
      setLoadingConsulta(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      consultarPlaca()
    }
  }

  const iniciarCadastroManual = () => {
    const placaNormalizada = normalizarPlaca(placaConsulta)
    if (placaNormalizada) {
      setValue("placa", placaNormalizada)
    }
    setCadastroManual(true)
  }

  // Mapeamento de marcas da API para valores do dropdown
  const mapeamentoMarcas: { [key: string]: string } = {
    'HYUNDAI': 'Hyundai',
    'VOLKSWAGEN': 'Volkswagen',
    'FORD': 'Ford',
    'CHEVROLET': 'Chevrolet',
    'FIAT': 'Fiat',
    'TOYOTA': 'Toyota',
    'HONDA': 'Honda',
    'NISSAN': 'Nissan',
    'RENAULT': 'Renault',
    'PEUGEOT': 'Peugeot',
    'BMW': 'BMW',
    'MERCEDES-BENZ': 'Mercedes-Benz',
    'AUDI': 'Audi',
    'VOLVO': 'Volvo',
    'LAND ROVER': 'Land Rover',
    'JAGUAR': 'Jaguar',
    'PORSCHE': 'Porsche',
    'LEXUS': 'Lexus',
    'INFINITI': 'Infiniti',
    'MASERATI': 'Maserati',
    'FERRARI': 'Ferrari',
    'LAMBORGHINI': 'Lamborghini',
    'BENTLEY': 'Bentley',
    'ROLLS-ROYCE': 'Rolls-Royce',
    'ASTON MARTIN': 'Aston Martin',
    'MCLAREN': 'McLaren',
    'CITROEN': 'Citroën',
    'DS AUTOMOBILES': 'DS Automobiles',
    'ALFA ROMEO': 'Alfa Romeo',
    'MINI': 'Mini',
    'SMART': 'Smart',
    'SEAT': 'Seat',
    'SKODA': 'Skoda',
    'OPEL': 'Opel',
    'LANCIA': 'Lancia',
    'BUGATTI': 'Bugatti',
    'JEEP': 'Jeep',
    'DODGE': 'Dodge',
    'CHRYSLER': 'Chrysler',
    'CADILLAC': 'Cadillac',
    'LINCOLN': 'Lincoln',
    'BUICK': 'Buick',
    'GMC': 'GMC',
    'RAM': 'Ram',
    'TESLA': 'Tesla',
    'RIVIAN': 'Rivian',
    'MITSUBISHI': 'Mitsubishi',
    'SUBARU': 'Subaru',
    'MAZDA': 'Mazda',
    'SUZUKI': 'Suzuki',
    'ISUZU': 'Isuzu',
    'KIA': 'Kia',
    'SSANGYONG': 'SsangYong',
    'MAHINDRA': 'Mahindra',
    'TATA': 'Tata',
    'CHERY': 'Chery',
    'JAC': 'JAC',
    'BYD': 'BYD',
    'GWM': 'GWM (Great Wall Motors)',
    'HAVAL': 'Haval',
    'CHANGAN': 'Changan',
    'GEELY': 'Geely',
    'LIFAN': 'Lifan',
    'BAIC': 'BAIC',
    'DONGFENG': 'Dongfeng',
    'FAW': 'FAW',
    'JMC': 'JMC',
    'IVECO': 'Iveco',
    'SCANIA': 'Scania',
    'MAN': 'MAN',
    'AGRALE': 'Agrale',
    'INTERNATIONAL': 'International',
    'KENWORTH': 'Kenworth',
    'PETERBILT': 'Peterbilt',
    'MACK': 'Mack',
    'YAMAHA': 'Yamaha',
    'KAWASAKI': 'Kawasaki',
    'HARLEY-DAVIDSON': 'Harley-Davidson',
    'DUCATI': 'Ducati',
    'TRIUMPH': 'Triumph',
    'ROYAL ENFIELD': 'Royal Enfield',
    'CAOA CHERY': 'Caoa Chery',
    'FOTON': 'Foton',
    'SHINERAY': 'Shineray',
    'EFFA': 'Effa',
    'JINBEI': 'Jinbei'
  }

  // Mapeamento de cores da API para valores do dropdown
  const mapeamentoCores: { [key: string]: string } = {
    'Prata': 'prata',
    'Branco': 'branco',
    'Preto': 'preto',
    'Cinza': 'cinza',
    'Azul': 'azul',
    'Vermelho': 'vermelho',
    'Verde': 'verde',
    'Amarelo': 'amarelo',
    'Marrom': 'marrom',
    'Dourado': 'dourado'
  }

  // Função para mapear códigos da situação do veículo para textos completos
  const mapearSituacaoVeiculo = (codigo: string) => {
    if (!codigo) return '';
    
    const mapeamento: { [key: string]: string } = {
      'S': 'Regular',
      'R': 'Roubado',
      'I': 'Irregular',
      'N': 'Não informado'
    };
    
    return mapeamento[codigo] || codigo;
  }

  const campo = (api: ApiVeiculo, novo: string, antigo?: string) =>
    String(api[novo] ?? (antigo ? api[antigo] : undefined) ?? '')

  // Função para mapear dados da API para o formato do banco
  const mapearDadosParaCadastro = (api: ApiVeiculo) => {

    const mapearCombustivel = (combustivel: string) => {
      if (!combustivel) return 'gasolina'
      const valor = combustivel.toLowerCase().replace(/[\s\-_/\\]+/g, '').normalize('NFD').replace(/[̀-ͯ]/g, '')
      if (['alcoolgasolina','alcoolegasolina','alcoolgas','alcoogasolina'].includes(valor)) return 'alcool_gasolina'
      const combustiveis: Record<string, string> = {
        gasolina: 'gasolina', alcool: 'etanol', etanol: 'etanol', flex: 'flex',
        diesel: 'diesel', gnv: 'gnv', eletrico: 'eletrico', hibrido: 'hibrido'
      }
      return combustiveis[valor] || 'gasolina'
    }

    const corRaw = String(campo(api, 'cor', 'COR') || api.extra?.cor_veiculo?.cor || '')
    const corNormalizada = (mapeamentoCores[corRaw] || corRaw || 'branco').toLowerCase()
    const marcaRaw = campo(api, 'marca', 'MARCA') || ''
    const marcaNormalizada = mapeamentoMarcas[marcaRaw] || mapeamentoMarcas[marcaRaw.toUpperCase()] || marcaRaw

    return {
      placa: campo(api, 'placa'),
      marca: marcaNormalizada,
      modelo: campo(api, 'modelo', 'MODELO'),
      versao: campo(api, 'versao', 'VERSAO') || campo(api, 'submodelo', 'SUBMODELO'),
      ano: parseInt(String(api.ano_fabricacao ?? campo(api, 'ano'))) || new Date().getFullYear(),
      anoModelo: campo(api, 'ano_modelo', 'anoModelo'),
      cor: corNormalizada,
      combustivel: mapearCombustivel(campo(api, 'combustivel') || api.extra?.combustivel || ''),
      cambio: 'manual',
      quilometragem: 0,
      motor: String(api.numero_motor ?? api.extra?.motor ?? campo(api, 'motor')),
      potencia: String(campo(api, 'potencia') || api.extra?.potencia || ''),
      capacidadeCarga: api.extra?.capacidade_carga ? String(api.extra.capacidade_carga) : '',
      passageiros: String(api.quantidade_lugares ?? api.extra?.quantidade_passageiro ?? campo(api, 'quantidade_passageiro') ?? ''),
      tipoVeiculo: api.extra?.tipo_veiculo?.tipo_veiculo || campo(api, 'tipo_veiculo') || '',
      cidade: campo(api, 'cidade') || api.extra?.municipio?.municipio || '',
      estado: campo(api, 'uf_jurisdicao') || campo(api, 'uf_placa') || campo(api, 'uf') || api.extra?.uf_placa || '',
      situacaoVeiculo: mapearSituacaoVeiculo(
        api.extra?.situacao_veiculo_descricao || campo(api, 'situacao_veiculo_descricao') ||
        campo(api, 'situacao_veiculo') || api.extra?.situacao_veiculo || ''
      ),
      portas: Number(api.quantidade_portas ?? 4),
      observacoes: `Veículo cadastrado via consulta de placa. Chassi: ${campo(api, 'chassi') || api.extra?.chassi || 'Não informado'}`
    }
  }
  // Função para cadastrar o veículo a partir da consulta
  const cadastrarVeiculoConsulta = useCallback(async () => {
    
    if (!veiculoConsulta) {
      return
    }
    
    try {
      // Mapear dados para o formato do banco
      const dadosVeiculo = mapearDadosParaCadastro(veiculoConsulta)

      // Preencher campos do formulário
      setValue("placa", dadosVeiculo.placa || "")
      setValue("marca", dadosVeiculo.marca || "")
      setValue("modelo", dadosVeiculo.modelo || "")
      setValue("versao", dadosVeiculo.versao || "")
      setValue("ano", dadosVeiculo.ano?.toString() || "")
      setValue("anoModelo", dadosVeiculo.anoModelo || "")
      setValue("cor", dadosVeiculo.cor || "")
      setValue("combustivel", dadosVeiculo.combustivel || "")
      setValue("cambio", dadosVeiculo.cambio || "")
      setValue("quilometragem", dadosVeiculo.quilometragem?.toString() || "")
      setValue("motor", dadosVeiculo.motor || "")
      setValue("potencia", dadosVeiculo.potencia || "")
      setValue("capacidadeCarga", dadosVeiculo.capacidadeCarga || "")
      setValue("passageiros", dadosVeiculo.passageiros || "")
      setValue("tipoVeiculo", dadosVeiculo.tipoVeiculo || "")
      setValue("cidade", dadosVeiculo.cidade || "")
      setValue("estado", dadosVeiculo.estado || "")
      setValue("situacaoVeiculo", dadosVeiculo.situacaoVeiculo || "")
      setValue("portas", dadosVeiculo.portas?.toString() || "4")
      setValue("observacoes", dadosVeiculo.observacoes || "")
      
      // Preencher chassi se disponível
      const chassi = veiculoConsulta.chassi || veiculoConsulta.extra?.chassi
      if (chassi) {
        setValue("chassi", chassi)
      }
      
      if (usaFIPE && valorFIPE) {
        setValue("valorFIPE", valorFIPE)
      }
      
      toast.success("Dados carregados da consulta de placa!")
    } catch (error) {
      console.error("💥 Erro ao processar dados:", error)
      toast.error("Erro inesperado ao processar dados do veículo")
    }
  }, [veiculoConsulta, setValue, verificarVeiculoExistente]);

  // Estado para pré-preenchimento via Assistente IA
  const [iaPlacaInicial, setIaPlacaInicial] = useState<string | null>(null)

  // Ao montar, ler placa enviada pelo Assistente IA via location.state
  useEffect(() => {
    const placaIA = location.state?.placaPreenchidaPorIA as string | undefined
    if (!placaIA) return

    const placaNormalizada = normalizarPlaca(placaIA)
    setPlacaConsulta(placaNormalizada)
    setIaPlacaInicial(placaNormalizada)

    // Limpa o state para não re-disparar em remount ou refresh
    navigate(location.pathname, { replace: true, state: {} })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Quando placaConsulta corresponder ao valor enviado pela IA, disparar consulta automática
  useEffect(() => {
    if (
      iaPlacaInicial &&
      placaConsulta === iaPlacaInicial &&
      !loadingConsulta &&
      !dadosCarregados
    ) {
      setIaPlacaInicial(null)
      void consultarPlaca()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placaConsulta, iaPlacaInicial])

  // Carregar dados da consulta se disponíveis
  useEffect(() => {
    const dadosConsulta = location.state?.dadosConsulta
    if (dadosConsulta) {
      
      // Preencher campos básicos
      setValue("placa", dadosConsulta.placa || "")
      setValue("marca", dadosConsulta.marca || "")
      setValue("modelo", dadosConsulta.modelo || "")
      setValue("ano", dadosConsulta.ano?.toString() || "")
      setValue("cor", dadosConsulta.cor || "")
      setValue("combustivel", dadosConsulta.combustivel || "")
      setValue("cambio", dadosConsulta.cambio || "")
      setValue("quilometragem", dadosConsulta.quilometragem?.toString() || "")
      setValue("motor", dadosConsulta.motor || "")
      setValue("portas", dadosConsulta.portas?.toString() || "4")
      setValue("observacoes", dadosConsulta.observacoes || "")
      setValue("chassi", "")
      setValue("renavam", "")
      
      setDadosCarregados(true)
      setCadastroManual(false)
      toast.success("Dados carregados da consulta de placa!")
    }
  }, [location.state, setValue])

  // Preencher automaticamente os campos quando a consulta retornar dados
  useEffect(() => {
    if (veiculoConsulta) {
      cadastrarVeiculoConsulta();
    }
  }, [veiculoConsulta, cadastrarVeiculoConsulta]);

  const onSubmit = async (data: VehicleFormData) => {
    if (submitInFlightRef.current) {
      return
    }

    submitInFlightRef.current = true
    setIsSubmitting(true)
    let releaseSubmitLock = true

    try {
      // Validação adicional para campos obrigatórios (apenas placa, marca, modelo, ano)
      const requiredFields = ['placa', 'marca', 'modelo', 'ano'];
      const missingFields = requiredFields.filter(field => !data[field as keyof VehicleFormData]);

      if (missingFields.length > 0) {
        toast.error(`Preencha os campos obrigatórios: ${missingFields.join(', ')}`)
        return
      }

      const placaNormalizada = normalizarPlaca(data.placa)
      if (!validarPlaca(placaNormalizada)) {
        toast.error("Placa inválida. Use o formato AAA0A00 ou AAA0000.")
        return
      }

      const combustivelNormalizado = normalizarCombustivel(data.combustivel)
      if (data.combustivel && !combustivelNormalizado) {
        toast.error("Combustível inválido. Use gasolina, etanol, flex, diesel, gnv, eletrico ou hibrido.")
        return
      }

      const valorFIPEInformado = parsearMoeda(data.valorFIPE || "")
      if (usaFIPE && data.valorFIPE?.trim() && valorFIPEInformado <= 0) {
        toast.error("Informe um valor FIPE válido ou deixe o campo vazio.")
        return
      }

      // Verificar se o veículo já existe
      const existe = await verificarVeiculoExistente(placaNormalizada)
      if (existe) {
        toast.error("Este veículo já está cadastrado no sistema")
        return
      }

      if (!loja?.id) {
        toast.error("Loja não encontrada")
        return
      }
      
      // Insert vehicle data (todos os campos disponíveis na tabela)
      const { data: vehicleData, error: vehicleError } = await supabase
        .from('veiculos')
        .insert({
          loja_id: loja.id,
          placa: placaNormalizada,
          marca: data.marca,
          modelo: data.modelo,
          versao: data.versao || null,
          ano: parseInt(data.ano),
          anomodelo: data.anoModelo || null,
          cor: data.cor || null,
          combustivel: combustivelNormalizado,
          cambio: data.cambio ? (data.cambio === 'automatica' ? 'automatico' : data.cambio) : null,
          quilometragem: data.quilometragem ? parseInt(data.quilometragem) : null,
          motor: data.motor || null,
          potencia: data.potencia || null,
          capacidadecarga: data.capacidadeCarga ? parseInt(data.capacidadeCarga) : null,
          passageiros: data.passageiros ? parseInt(data.passageiros) : null,
          tipoveiculo: data.tipoVeiculo || null,
          cidade: data.cidade || null,
          estado: data.estado || null,
          situacaoveiculo: data.situacaoVeiculo || null,
          portas: data.portas ? parseInt(data.portas) : null,
          observacoes: data.observacoes || null,
          chassi: data.chassi || null,
          renavam: data.renavam || null,
          foto_principal: null
        } satisfies TablesInsert<'veiculos'>)
        .select()
        .single()


      if (vehicleError) {
        console.error("Erro ao cadastrar veículo:", vehicleError)
        if (vehicleError.code === '23505') {
          toast.error("Este veículo já está cadastrado nesta loja.")
        } else {
          toast.error(getPublicErrorMessage(vehicleError.message, "Não foi possível cadastrar o veículo agora."))
        }
        return
      }

      if (vehicleData && fotoPrincipal) {
        let fotoPrincipalPath: string | null = null

        if (typeof fotoPrincipal === 'string') {
          fotoPrincipalPath = fotoPrincipal || null
        } else if (fotoPrincipal.file) {
          const { path } = await uploadFileToBucket(
            PHOTO_BUCKET,
            fotoPrincipal.file,
            `lojas/${loja.id}/veiculos/${vehicleData.id}/capa`,
          )
          fotoPrincipalPath = path
        } else {
          fotoPrincipalPath = getUploadStoragePath(fotoPrincipal) ?? null
        }

        if (fotoPrincipalPath) {
          await supabase
            .from('veiculos')
            .update({ foto_principal: fotoPrincipalPath })
            .eq('id', vehicleData.id)
        }
      }

      // Insert documents if any
      if (documents.length > 0 && vehicleData) {
        const documentosSalvos = await Promise.all(
          documents.map(async (doc) => {
            if (doc.file) {
              const { path } = await uploadFileToBucket(
                DOCUMENT_BUCKET,
                doc.file,
                `lojas/${loja?.id}/veiculos/${vehicleData.id}/documentos`,
              )

              return {
                ...doc,
                storagePath: path,
              }
            }

            return doc
          }),
        )

        const documentsToInsert = documentosSalvos.map(doc => ({
          veiculo_id: vehicleData.id,
          tipo_documento: doc.tipo_documento,
          url: doc.storagePath ?? doc.url,
          nome_arquivo: doc.nome_arquivo
        }))


        const { error: documentsError } = await supabase
          .from('documentos_veiculo')
          .insert(documentsToInsert)


        if (documentsError) {
          toast.error("Veículo cadastrado, mas houve erro ao salvar documentos")
        }
      } else {
        console.log("ℹ️ Nenhum documento para inserir")
      }

      toast.success(
        usaFIPE ? "Veículo cadastrado com sucesso!" : "Veículo cadastrado",
        usaFIPE
          ? undefined
          : {
              description: "Próximo passo: registrar entrada do veículo.",
              action: {
                label: "Iniciar entrada agora",
                onClick: () => navigate(`/veiculo/${vehicleData.id}`),
              },
            }
      )
      
      if (usaFIPE) {
        const valorFipeParaHistorico = valorFIPEInformado > 0 ? valorFIPEInformado : valorFIPENumerico
        if (valorFipeParaHistorico !== null && valorFipeParaHistorico > 0 && vehicleData?.id) {
          try {
            const { salvarHistoricoFIPE } = await import('@/lib/fipeService')
            const origem = valorFIPENumerico !== null && Math.abs(valorFipeParaHistorico - valorFIPENumerico) < 0.01
              ? 'api'
              : 'manual'
            await salvarHistoricoFIPE(vehicleData.id, valorFipeParaHistorico, placaNormalizada, origem)
          } catch {
            // Não interrompe o fluxo
          }
        }
      }
      
      // Aguardar o usuário ver a mensagem de confirmação antes do redirecionamento
      releaseSubmitLock = false
      setTimeout(() => {
        navigate(`/veiculo/${vehicleData.id}`)
      }, usaFIPE ? 1500 : 700)
    } catch (error) {
      console.error("💥 ERRO INESPERADO no cadastro:", error)
      toast.error("Erro inesperado ao cadastrar veículo")
    } finally {
      if (releaseSubmitLock) {
        submitInFlightRef.current = false
        setIsSubmitting(false)
      }
    }
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i)
  const mostrarFormularioCadastro = dadosCarregados || cadastroManual
  const modoResultadoConsulta = Boolean((veiculoConsulta || (usaFIPE && valorFIPE)) && !cadastroManual)
  const modoFormularioInline = mostrarFormularioCadastro && !modoResultadoConsulta

  // JSX compartilhado para o combobox de marca (usado em dois modos)
  const renderMarcaField = () => (
    <div className="space-y-2">
      <Label htmlFor="marca">Marca *</Label>
      <Controller
        name="marca"
        control={control}
        rules={{ required: "Marca é obrigatória" }}
        render={({ field }) => {
          const filteredBrands = filterBrands(brandSearchValue)
          return (
            <Popover open={brandOpen} onOpenChange={setBrandOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={brandOpen}
                  className={cn("w-full justify-between", errors.marca && "border-destructive", !field.value && "text-muted-foreground")}
                >
                  {field.value ? vehicleBrands.find((b) => b === field.value) : "Selecione ou digite a marca"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Buscar marca..." value={brandSearchValue} onValueChange={setBrandSearchValue} />
                  <CommandList>
                    <CommandEmpty>
                      {brandSearchValue ? (
                        <div className="py-6 text-center text-sm">
                          <p>Marca não encontrada</p>
                          <p className="text-muted-foreground">Use "{brandSearchValue}" como marca personalizada</p>
                          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => { field.onChange(brandSearchValue); setBrandOpen(false); setBrandSearchValue("") }}>
                            Usar "{brandSearchValue}"
                          </Button>
                        </div>
                      ) : ("Nenhuma marca encontrada.")}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredBrands.map((brand) => (
                        <CommandItem key={brand} value={brand} onSelect={() => { field.onChange(brand); setBrandOpen(false); setBrandSearchValue("") }}>{brand}</CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )
        }}
      />
      {errors.marca && <p className="text-sm text-destructive">Marca é obrigatória</p>}
    </div>
  )

  const renderIdentificacaoFields = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="placa">Placa *</Label>
          <Input
            id="placa"
            placeholder="ABC-1234"
            {...register("placa", { required: "Placa é obrigatória" })}
            className={errors.placa ? "border-destructive" : ""}
          />
          {errors.placa && <p className="text-sm text-destructive">{errors.placa.message}</p>}
        </div>
        {renderMarcaField()}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="modelo">Modelo *</Label>
          <Input
            id="modelo"
            placeholder="Ex: Civic, Corolla, Golf"
            {...register("modelo", { required: "Modelo é obrigatório" })}
            className={errors.modelo ? "border-destructive" : ""}
          />
          {errors.modelo && <p className="text-sm text-destructive">{errors.modelo.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="versao">Versão</Label>
          <Input id="versao" placeholder="Ex: EXL, LTZ, XEi" {...register("versao")} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="ano">Ano de Fabricação *</Label>
          <Controller
            name="ano"
            control={control}
            rules={{ required: "Ano de fabricação é obrigatório" }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className={errors.ano ? "border-destructive" : ""}>
                  <SelectValue placeholder="Selecione o ano" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.ano && <p className="text-sm text-destructive">Ano de fabricação é obrigatório</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="anoModelo">Ano do Modelo</Label>
          <Input id="anoModelo" placeholder="Ex: 2025" {...register("anoModelo")} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="chassi">Chassi</Label>
          <Input id="chassi" placeholder="Ex: 1HGBH41JXMN109186" {...register("chassi")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="renavam">Renavam</Label>
          <Input id="renavam" placeholder="Ex: 12345678901" {...register("renavam")} />
        </div>
      </div>
    </div>
  )

  const renderCaracteristicasFields = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="cor">Cor</Label>
          <Controller
            name="cor"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a cor (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="branco">Branco</SelectItem>
                  <SelectItem value="preto">Preto</SelectItem>
                  <SelectItem value="prata">Prata</SelectItem>
                  <SelectItem value="cinza">Cinza</SelectItem>
                  <SelectItem value="azul">Azul</SelectItem>
                  <SelectItem value="vermelho">Vermelho</SelectItem>
                  <SelectItem value="verde">Verde</SelectItem>
                  <SelectItem value="amarelo">Amarelo</SelectItem>
                  <SelectItem value="marrom">Marrom</SelectItem>
                  <SelectItem value="dourado">Dourado</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="combustivel">Combustível</Label>
          <Input
            id="combustivel"
            placeholder="Ex: Gasolina, Etanol, Flex, Diesel, GNV, Elétrico, Híbrido, Álcool/Gasolina"
            {...register("combustivel")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cambio">Transmissão</Label>
          <Controller
            name="cambio"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a transmissão (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="automatica">Automática</SelectItem>
                  <SelectItem value="cvt">CVT</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="potencia">Potência</Label>
          <Input id="potencia" placeholder="Ex: 120 cv" {...register("potencia")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="capacidadeCarga">Capacidade de Carga (kg)</Label>
          <Input id="capacidadeCarga" placeholder="Ex: 500" {...register("capacidadeCarga")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="passageiros">Passageiros</Label>
          <Input id="passageiros" placeholder="Ex: 5" {...register("passageiros")} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="tipoVeiculo">Tipo de Veículo</Label>
          <Input id="tipoVeiculo" placeholder="Ex: SUV, Sedan" {...register("tipoVeiculo")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cidade">Cidade</Label>
          <Input id="cidade" placeholder="Ex: São Paulo" {...register("cidade")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="estado">Estado</Label>
          <Input id="estado" placeholder="Ex: SP" {...register("estado")} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="situacaoVeiculo">Situação do Veículo</Label>
          <Input id="situacaoVeiculo" placeholder="Ex: Regular, Roubado, Irregular" {...register("situacaoVeiculo")} />
        </div>
        {usaFIPE && (
          <div className="space-y-2">
            <Label htmlFor="valorFIPE">Valor FIPE</Label>
            <Input
              id="valorFIPE"
              placeholder="Ex: R$ 85.000,00"
              {...register("valorFIPE", {
                onBlur: (event) => {
                  const valor = parsearMoeda(event.target.value)
                  event.target.value = valor > 0 ? formatarMoedaInput(valor) : ""
                },
              })}
            />
            <p className="text-xs text-muted-foreground">
              Preencha manualmente se a consulta automática não retornar FIPE.
            </p>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="quilometragem">Quilometragem (km)</Label>
          <Input id="quilometragem" placeholder="Deixe vazio se 0km" type="number" {...register("quilometragem")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="motor">Motor</Label>
          <Input id="motor" placeholder="Ex: 1.0, 1.6, 2.0 (opcional)" {...register("motor")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="portas">Número de Portas</Label>
          <Controller
            name="portas"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o número de portas (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 portas</SelectItem>
                  <SelectItem value="3">3 portas</SelectItem>
                  <SelectItem value="4">4 portas</SelectItem>
                  <SelectItem value="5">5 portas</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
    </div>
  )

  const renderDocumentacaoFields = () => (
    <DocumentUpload
      documents={documents}
      onDocumentsUpdate={(updatedDocuments: UploadableDocument[]) => {
        setFoiRevisado(true)
        setDocuments(updatedDocuments)
      }}
      maxDocuments={10}
      disabled={isSubmitting}
    />
  )

  const renderObservacoesFields = () => (
    <div className="space-y-2">
      <Label htmlFor="observacoes">Observações</Label>
      <Textarea
        id="observacoes"
        placeholder="Adicione informações relevantes sobre o veículo..."
        className="min-h-[160px]"
        {...register("observacoes")}
      />
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/veiculos">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Cadastrar Novo Veículo</h1>
          <p className="text-muted-foreground">Comece pela placa. Se preferir, também pode preencher manualmente.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => { if (modoResultadoConsulta) setRevisaoAberta(true) })} className="space-y-6">
        {/* Alerta de dados carregados */}
        {dadosCarregados && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Dados carregados da consulta de placa. Você pode editar as informações antes de salvar.
            </AlertDescription>
          </Alert>
        )}

        {/* Formulário de Consulta */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>Consultar por placa</CardTitle>
              <Badge variant="outline" className="border-primary/20 bg-background text-primary">
                Recomendado
              </Badge>
            </div>
            <CardDescription>
              Os dados do veículo são preenchidos automaticamente para reduzir o trabalho manual.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-4 md:flex-row md:items-end">
                <div className="flex-1">
                  <label htmlFor="placa-consulta" className="text-sm font-medium mb-2 block">
                    Placa do Veículo
                  </label>
                  <div className="relative">
                    <Car className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="placa-consulta"
                      placeholder="Ex: ABC1D23"
                      value={placaConsulta}
                      onChange={(e) => setPlacaConsulta(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                      onKeyPress={handleKeyPress}
                      className="pl-10 uppercase"
                      maxLength={7}
                      disabled={loadingConsulta}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={consultarPlaca}
                  disabled={loadingConsulta}
                  className="md:w-auto"
                >
                  {loadingConsulta ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Consultando...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Consultar
                    </>
                  )}
                </Button>
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  onClick={iniciarCadastroManual}
                  disabled={isSubmitting}
                  className="h-auto p-0 text-xs font-normal text-muted-foreground hover:text-foreground"
                >
                  Preencher manualmente
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resultados da Consulta */}
        {errorConsulta && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{errorConsulta.title}</AlertTitle>
            <AlertDescription>{errorConsulta.message}</AlertDescription>
          </Alert>
        )}

        {usaFIPE && errorFIPE && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{errorFIPE.title}</AlertTitle>
            <AlertDescription>{errorFIPE.message}</AlertDescription>
          </Alert>
        )}

        {(veiculoConsulta || (usaFIPE && valorFIPE)) && (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1.5">
                  <CardTitle className="flex flex-wrap items-center gap-2">
                    Resultado da Consulta
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Encontrado
                    </Badge>
                    {foiRevisado && (
                      <Badge variant="secondary" className="font-normal">
                        Dados revisados
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Dados do veículo consultado através da placa {placaConsulta}
                  </CardDescription>
                </div>
                {modoResultadoConsulta && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRevisaoAberta(true)}
                    className="h-8 gap-2 self-start border-muted-foreground/20 bg-background px-3 text-xs font-normal text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Revisar dados
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Valor FIPE em destaque — apenas para loja */}
              {usaFIPE && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-800">Valor FIPE</p>
                      <p className="text-2xl font-bold text-blue-900">{valorFIPE || '—'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMostrarHistorico(!mostrarHistorico)}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      {mostrarHistorico ? (
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {mostrarHistorico && historicoFIPE && historicoFIPE.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <p className="text-sm font-medium text-blue-800 mb-2">Histórico de Valores</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {historicoFIPE.map((item, index) => (
                          <div key={index} className="flex justify-between items-center py-2 px-3 bg-white rounded border">
                            <span className="text-sm text-gray-700">{item.mes}</span>
                            <span className="text-sm font-medium text-green-700">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              }).format(item.valor)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dados do veículo */}
              {veiculoConsulta && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Placa</p>
                    <p className="font-semibold">{veiculoConsulta.placa}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Chassi</p>
                    <p className="font-semibold">{veiculoConsulta.chassi || veiculoConsulta.extra?.chassi}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Marca</p>
                    <p className="font-semibold">{humanizarTextoTecnico(veiculoConsulta.marca || veiculoConsulta.MARCA) || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Modelo</p>
                    <p className="font-semibold">{humanizarTextoTecnico(veiculoConsulta.modelo || veiculoConsulta.MODELO) || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Ano de Fabricação</p>
                    <p className="font-semibold">{veiculoConsulta.ano_fabricacao ?? veiculoConsulta.ano}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Ano do Modelo</p>
                    <p className="font-semibold">{veiculoConsulta.ano_modelo ?? veiculoConsulta.anoModelo}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Cor</p>
                    <p className="font-semibold">{humanizarTextoTecnico(veiculoConsulta.cor || veiculoConsulta.extra?.cor_veiculo?.cor) || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Combustível</p>
                    <p className="font-semibold">{humanizarCombustivel(veiculoConsulta.combustivel || veiculoConsulta.extra?.combustivel) || '—'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Potência</p>
                    <p className="font-semibold">
                      {veiculoConsulta.potencia || veiculoConsulta.extra?.potencia
                        ? `${veiculoConsulta.potencia || veiculoConsulta.extra?.potencia} cv`
                        : '—'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Capacidade / Passageiros</p>
                    <p className="font-semibold">
                      {veiculoConsulta.extra?.capacidade_carga ? `${veiculoConsulta.extra.capacidade_carga} kg / ` : ''}
                      {veiculoConsulta.quantidade_lugares || veiculoConsulta.extra?.quantidade_passageiro || veiculoConsulta.quantidade_passageiro || '—'} lugares
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Tipo de Veículo</p>
                    <p className="font-semibold">
                      {typeof veiculoConsulta.tipo_veiculo === 'string'
                        ? humanizarTextoTecnico(veiculoConsulta.tipo_veiculo)
                        : humanizarTextoTecnico(veiculoConsulta.tipo_veiculo?.tipo_veiculo ||
                          veiculoConsulta.extra?.tipo_veiculo?.tipo_veiculo ||
                          '')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Cidade/Estado</p>
                    <p className="font-semibold">
                      {[veiculoConsulta.cidade || veiculoConsulta.extra?.municipio?.municipio, veiculoConsulta.uf_jurisdicao || veiculoConsulta.uf_placa || veiculoConsulta.uf || veiculoConsulta.extra?.uf_placa].filter(Boolean).join(' / ') || '—'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Número do Motor</p>
                    <p className="font-semibold">{veiculoConsulta.numero_motor || veiculoConsulta.extra?.motor || veiculoConsulta.motor}</p>
                  </div>
                  {veiculoConsulta.logo && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Logo</p>
                      <img src={veiculoConsulta.logo} alt={`Logo ${veiculoConsulta.marca || veiculoConsulta.MARCA}`} className="h-16 object-contain" />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {modoFormularioInline && (
          <>
        {/* Identificação do Veículo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              {cadastroManual ? "Identificação manual" : "Identificação conferida"}
            </CardTitle>
            <CardDescription>
              {cadastroManual
                ? "Preencha os dados mínimos para cadastrar o veículo."
                : "Revise os dados preenchidos automaticamente antes de salvar."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="placa">Placa *</Label>
                <Input
                  id="placa"
                  placeholder="ABC-1234"
                  {...register("placa", { required: "Placa é obrigatória" })}
                  className={errors.placa ? "border-destructive" : ""}
                />
                {errors.placa && (
                  <p className="text-sm text-destructive">{errors.placa.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="marca">Marca *</Label>
                <Controller
                  name="marca"
                  control={control}
                  rules={{ required: "Marca é obrigatória" }}
                  render={({ field }) => {
                    const filteredBrands = filterBrands(brandSearchValue)
                    
                    return (
                      <Popover open={brandOpen} onOpenChange={setBrandOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={brandOpen}
                            className={cn(
                              "w-full justify-between",
                              errors.marca && "border-destructive",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value
                              ? vehicleBrands.find((brand) => brand === field.value)
                              : "Selecione ou digite a marca"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput
                              placeholder="Buscar marca..."
                              value={brandSearchValue}
                              onValueChange={setBrandSearchValue}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {brandSearchValue ? (
                                  <div className="py-6 text-center text-sm">
                                    <p>Marca não encontrada</p>
                                    <p className="text-muted-foreground">
                                      Use "{brandSearchValue}" como marca personalizada
                                    </p>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="mt-2"
                                      onClick={() => {
                                        field.onChange(brandSearchValue)
                                        setBrandOpen(false)
                                        setBrandSearchValue("")
                                      }}
                                    >
                                      Usar "{brandSearchValue}"
                                    </Button>
                                  </div>
                                ) : (
                                  "Nenhuma marca encontrada."
                                )}
                              </CommandEmpty>
                              <CommandGroup>
                                {filteredBrands.map((brand) => (
                                  <CommandItem
                                    key={brand}
                                    value={brand}
                                    onSelect={() => {
                                      field.onChange(brand)
                                      setBrandOpen(false)
                                      setBrandSearchValue("")
                                    }}
                                  >
                                    {brand}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )
                  }}
                />
                {errors.marca && (
                  <p className="text-sm text-destructive">Marca é obrigatória</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="modelo">Modelo *</Label>
                <Input
                  id="modelo"
                  placeholder="Ex: Civic, Corolla, Golf"
                  {...register("modelo", { required: "Modelo é obrigatório" })}
                  className={errors.modelo ? "border-destructive" : ""}
                />
                {errors.modelo && (
                  <p className="text-sm text-destructive">{errors.modelo.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="versao">Versão</Label>
                <Input
                  id="versao"
                  placeholder="Ex: EXL, LTZ, XEi"
                  {...register("versao")}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ano">Ano de Fabricação *</Label>
                <Controller
                  name="ano"
                  control={control}
                  rules={{ required: "Ano de fabricação é obrigatório" }}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className={errors.ano ? "border-destructive" : ""}>
                        <SelectValue placeholder="Selecione o ano" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.ano && (
                  <p className="text-sm text-destructive">Ano de fabricação é obrigatório</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="anoModelo">Ano do Modelo</Label>
                <Input
                  id="anoModelo"
                  placeholder="Ex: 2025"
                  {...register("anoModelo")}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="chassi">Chassi</Label>
                <Input
                  id="chassi"
                  placeholder="Ex: 1HGBH41JXMN109186"
                  {...register("chassi")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="renavam">Renavam</Label>
                <Input
                  id="renavam"
                  placeholder="Ex: 12345678901"
                  {...register("renavam")}
                />
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Foto do Veículo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Foto do Veículo
            </CardTitle>
            <CardDescription>
              Adicione uma foto principal para reconhecer o veículo com facilidade.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PhotoUploadPrincipal
              photo={fotoPrincipal}
              onPhotoUpdate={setFotoPrincipal}
              disabled={isSubmitting}
            />
          </CardContent>
        </Card>

        {/* Características Técnicas */}
        <Card>
          <CardHeader>
            <CardTitle>Características Técnicas</CardTitle>
            <CardDescription>
              Especificações técnicas do veículo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="cor">Cor</Label>
                <Controller
                  name="cor"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a cor (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="branco">Branco</SelectItem>
                        <SelectItem value="preto">Preto</SelectItem>
                        <SelectItem value="prata">Prata</SelectItem>
                        <SelectItem value="cinza">Cinza</SelectItem>
                        <SelectItem value="azul">Azul</SelectItem>
                        <SelectItem value="vermelho">Vermelho</SelectItem>
                        <SelectItem value="verde">Verde</SelectItem>
                        <SelectItem value="amarelo">Amarelo</SelectItem>
                        <SelectItem value="marrom">Marrom</SelectItem>
                        <SelectItem value="dourado">Dourado</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="combustivel">Combustível</Label>
                <Input
                  id="combustivel"
                  placeholder="Ex: Gasolina, Etanol, Flex, Diesel, GNV, Elétrico, Híbrido, Alcool/Gasolina"
                  {...register("combustivel")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cambio">Transmissão</Label>
                <Controller
                  name="cambio"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a transmissão (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="automatica">Automática</SelectItem>
                        <SelectItem value="cvt">CVT</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="potencia">Potência</Label>
                <Input
                  id="potencia"
                  placeholder="Ex: 120 cv"
                  {...register("potencia")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacidadeCarga">Capacidade de Carga (kg)</Label>
                <Input
                  id="capacidadeCarga"
                  placeholder="Ex: 500"
                  {...register("capacidadeCarga")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="passageiros">Passageiros</Label>
                <Input
                  id="passageiros"
                  placeholder="Ex: 5"
                  {...register("passageiros")}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="tipoVeiculo">Tipo de Veículo</Label>
                <Input
                  id="tipoVeiculo"
                  placeholder="Ex: SUV, Sedan"
                  {...register("tipoVeiculo")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  placeholder="Ex: São Paulo"
                  {...register("cidade")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="estado">Estado</Label>
                <Input
                  id="estado"
                  placeholder="Ex: SP"
                  {...register("estado")}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="situacaoVeiculo">Situação do Veículo</Label>
                <Input
                  id="situacaoVeiculo"
                  placeholder="Ex: Regular, Roubado, Irregular"
                  {...register("situacaoVeiculo")}
                />
              </div>
              {usaFIPE && (
                <div className="space-y-2">
                  <Label htmlFor="valorFIPE">Valor FIPE</Label>
                  <Input
                    id="valorFIPE"
                    placeholder="Ex: R$ 85.000,00"
                    {...register("valorFIPE", {
                      onBlur: (event) => {
                        const valor = parsearMoeda(event.target.value)
                        event.target.value = valor > 0 ? formatarMoedaInput(valor) : ""
                      },
                    })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Preencha manualmente se a consulta automática não retornar FIPE.
                  </p>
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="quilometragem">Quilometragem (km)</Label>
                <Input
                  id="quilometragem"
                  placeholder="Deixe vazio se 0km"
                  type="number"
                  {...register("quilometragem")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="motor">Motor</Label>
                <Input
                  id="motor"
                  placeholder="Ex: 1.0, 1.6, 2.0 (opcional)"
                  {...register("motor")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="portas">Número de Portas</Label>
                <Controller
                  name="portas"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o número de portas (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 portas</SelectItem>
                        <SelectItem value="3">3 portas</SelectItem>
                        <SelectItem value="4">4 portas</SelectItem>
                        <SelectItem value="5">5 portas</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documentação do Veículo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documentação do Veículo
            </CardTitle>
            <CardDescription>
              Faça upload dos documentos do veículo (CRLV, laudos, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DocumentUpload
              documents={documents}
              onDocumentsUpdate={setDocuments}
              maxDocuments={10}
              disabled={isSubmitting}
            />
          </CardContent>
        </Card>

        {/* Observações */}
        <Card>
          <CardHeader>
            <CardTitle>Observações Adicionais</CardTitle>
            <CardDescription>
              Informações extras sobre o veículo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Adicione informações relevantes sobre o veículo..."
                className="min-h-[100px]"
                {...register("observacoes")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Botões de Ação */}
        <div className="flex gap-4 justify-end">
          <Button variant="outline" asChild>
            <Link to="/veiculos">Cancelar</Link>
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? "Cadastrando..." : "Cadastrar Veículo"}
          </Button>
        </div>
          </>
        )}

        {modoResultadoConsulta && (
          <>
            {revisaoAberta && (
              <div className="space-y-4 rounded-lg border border-primary/15 bg-muted/20 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Revisar dados do veículo</h2>
                    <p className="text-sm text-muted-foreground">
                      Edite apenas o que precisar. O cadastro mantém os mesmos dados preenchidos pela consulta.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRevisaoAberta(false)}
                    className="self-start"
                  >
                    Fechar revisão
                  </Button>
                </div>
                <VeiculoFormSections
                  register={register}
                  control={control}
                  errors={errors}
                  years={years}
                  documents={documents}
                  onDocumentsUpdate={(updatedDocuments) => {
                    setFoiRevisado(true)
                    setDocuments(updatedDocuments)
                  }}
                  disabled={isSubmitting}
                  brandOpen={brandOpen}
                  setBrandOpen={setBrandOpen}
                  brandSearchValue={brandSearchValue}
                  setBrandSearchValue={setBrandSearchValue}
                  showPhoto={false}
                  showValorFIPE={usaFIPE}
                  identificationTitle="Identificação conferida"
                  identificationDescription="Revise os dados preenchidos automaticamente antes de salvar."
                  onAnyInteraction={() => setFoiRevisado(true)}
                />
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Foto do Veículo
                </CardTitle>
                <CardDescription>
                  Adicione uma foto principal para reconhecer o veículo com facilidade.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PhotoUploadPrincipal
                  photo={fotoPrincipal}
                  onPhotoUpdate={setFotoPrincipal}
                  disabled={isSubmitting}
                />
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-end">
              <Button variant="outline" asChild>
                <Link to="/veiculos">Cancelar</Link>
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                <Save className="mr-2 h-4 w-4" />
                {isSubmitting ? "Cadastrando..." : "Cadastrar Veículo"}
              </Button>
            </div>

          </>
        )}
      </form>
    </div>
  )
}

export default NovoVeiculo
